import { useState, useEffect } from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect, RootStateOrAny } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import moment from 'moment';
import * as XLSX from 'xlsx';

import { Breadcrumb, Typography, Tag, Button } from 'antd';
import { CloudDownloadOutlined, HomeOutlined, SearchOutlined, UserOutlined } from '@ant-design/icons';

import { formatPhoneNumber, sortByIPA, getDisplayCompanyId } from 'utils/helpers';
import { AuthModal, ErrorResponse, Attachment, UserModel, TablePagination } from 'utils/types';
import notifier from 'utils/notifier';
import { Color, StatusCode } from 'utils/enums';
import { H5499_POS_CODES} from 'utils/objects';

import SearchAuthorizationTable from 'components/Auth/SearchAuthorizationTable';
import { getPortalAuth } from 'store/authorization/portal/actions';
import { getAttachInfo } from 'store/authorization/attach/actions';
import { getAuthEnabledProviderList, GetAuthEnabledProviderListResponse } from 'store/authorization/provider/actions';
import { modifyAuth, ModifyModal } from 'store/authorization/extendAndModify/actions';
import { GetAuthList, getAuthList } from 'store/authorization/list/actions';
import { GetSubmittedFileNames, getSubmittedFileNames, uploadFilestoAuth } from 'store/authorization/files/actions';
import { getHelpCenterLinks } from 'store/app/helpCenter/actions';

import AuthDetail from 'components/Auth/Detail';
import SearchAuthForm from 'components/Auth/SearchAuthForm';
import AttachmentFormModal from 'components/Auth/Attachment';
import HelpCenterLink from 'components/Common/HelpCenterLink';

import styles from './SearchAuthorizaton.module.scss';

// TODO: Refactor

interface SearchAuthPageProps {
  getAuthEnabledProviderList(userModel: UserModel): any;
  getPortalAuth(authNo: string, company: string, userRole: number): any,
  getAttachInfo(authNo: string, companyId: string): any,
  modifyAuth(model: ModifyModal): any,
  getAuthList(userModel: GetAuthList): any,
  getSubmittedFileNames(model: GetSubmittedFileNames): any,
  uploadFilestoAuth(options: any): any,
  isFetchingList: boolean,
  isFetchingListGroup: boolean,
  isFetchingAuthList: boolean,
  isUploadingFilesToAuth: boolean,
  getHelpCenterLinks(companyId: string, page: string, type: string): any;
}

const { Title } = Typography;

const SearchAuthPage = (props: SearchAuthPageProps) => {
  const history = useHistory();
  const location: any = useLocation();
  const [providerList, setProviderList] = useState([] as any);
  const [tableInfo, setTableInfo] = useState({});
  const [searchResult, setSearchResult] = useState([]);
  const [totalResultCount, setTotalResultCount] = useState(0);
  const [authDetail, setAuthDetail] = useState({});
  const [state, setState] = useState('start');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [showHospitalDates, setShowHospitalDates] = useState(false);
  const [helpCenter, setHelpCenter] = useState<any>([]);

  const [searchAuthPageState, setSearchAuthPageState] = useState({
    authNo: '',
    healthPlanAuthNumber: '',
    requestDate: '',
    referenceType: '',
    certificateType: '',
    actionDate: '',
    expirationDate: '',
    retroDate: '',
    companyId: '',
    status: '',
    memberName: '',
    memberPhoneNumber: '',
    healthPlan: '',
    dateOfBirth: '',
    gender: '',
    memberId: '',
    memberPcp: '',
    referralBy: '',
    referralByNumber: '',
    referralTo: '',
    referralSpecialty: '',
    referralPos: '',
    referralByFaxNumber: '',
    memberAddress: '',
    memberCity: '',
    memberState: '',
    memberZipCode: '',
    referralToAddress: '',
    referralToCity: '',
    referralToState: '',
    referralToZipCode: '',
    referralToPhoneNumber: '',
    referralToFaxNumber: '',
    facilityName: '',
    facilityAddress: '',
    facilityCity: '',
    facilityState: '',
    facilityZipCode: '',
    facilityPhoneNumber: '',
    authDiagsData: [{}],
    authDetailsData: [{}],
    authNotesData: [{}],
    formColumnData: [{}],
    letterInfos: [{}],
    showNotes: false,
    attachments: [{}],
    authPcpId: '',
    requestProv: '',
    authPcpFirstName: '',
    authPcpLastName: '',
    requestProviderFirstName: '',
    requestProviderLastName: '',
    memberFirstName: '',
    memberLastName: '',
    priorityStatus: ''
  } as AuthModal);


  const columns = [
    {
      title: 'IPA',
      key: 'companyId',
      dataIndex: 'companyId',
      render: (text: string) => getDisplayCompanyId(text)
    },
    {
      title: 'Auth Number',
      key: 'authNumSorter',
      dataIndex: 'authNumSorter',
      sorter: (a: any, b: any) => a.authNumSorter.localeCompare(b.authNumSorter),
      render: (_: string, record: any) => <a href="#/" onClick={() => triggerAuthDetailPage({companyId: record.companyId, authNo: record.authNumSorter})}>{record.authNumSorter}</a>
    },
    {
      title: 'Member ID',
      key: 'memberRefId',
      dataIndex: 'memberRefId',
      sorter: (a: any, b: any) => a.memberRefId.localeCompare(b.memberRefId),
    },
    {
      title: 'Member Name',
      key: 'name',
      dataIndex: 'name',
      sorter: (a: any, b: any) => a.name.localeCompare(b.name),
    },
    {
      title: 'Refer to Provider',
      key: 'providerRef',
      dataIndex: 'providerRef',
    },
    {
      title: 'Status',
      key: 'status',
      dataIndex: 'status',
      render: (_: string, record: any) => {
        return (
          <>
            { record.status === 'APPROVED' && <Tag color={Color.APPROVED}>APPROVED</Tag> }
            { record.status === 'MODIFIED' && <Tag color={Color.MODIFIED}>MODIFIED</Tag> }
            { record.status === 'DEFERRED' && <Tag color={Color.DEFERRED}>DEFERRED</Tag> }
            { record.status === 'DENIED' && <Tag color={Color.DENIED}>DENIED</Tag> }
            { record.status === 'CANCELLED' && <Tag color={Color.CANCELLED}>CANCELLED</Tag> }
            { record.status === 'EXPIRED' && <Tag color={Color.EXPIRED}>EXPIRED</Tag> }
            { record.status === 'PENDED' && <Tag color={Color.PENDED}>PENDED</Tag>}
            { record.status === 'REQUESTED' && <Tag style={{ "color": Color.REQUESTED_TEXT}}  color={Color.REQUESTED}>REQUESTED</Tag> }
            { record.status === 'SYSTEM_HOLD' && <Tag style={{ "color": Color.SYSTEM_HOLD_TEXT}} color={Color.SYSTEM_HOLD}>SYSTEM HOLD</Tag> }
          </>
        );
      }
    },
    {
      title: 'Expiration Date',
      key: 'expDate',
      dataIndex: 'expDate',
    },
    {
      title: 'Attachment',
      key: 'attach',
      dataIndex: 'attach',
    },
  ];

  const attachmentsFormModal = (apiData: any) => {
    props.getPortalAuth(apiData.authNum, apiData.companyId, parseInt(sessionStorage.getItem('user_role')!))
    .then((data: any) => {

      const authDiagsData: Array<Object> = [];
      const authDetailsData: Array<Object> = [];
      const authNotesData: Array<Object> = [];

      //need to split this into array because the API doesn't provide first and last name for these fields
      const memberFullName = data.RmiMembName.split(',');
      const authPcpFullName = data.RmiPcpRevFullName.split(',');
      const requestProviderFullName = data.RmiReqRevFullName.split(',');

      //From the legacy code, it shows there are 6 different dialog types that are mixed inside AuthDiags response
      for(const key in data.AuthDiags) {
        if(data.AuthDiags[key].RmiDiag !== 'RR' && data.AuthDiags[key].RmiDiag !== 'MR' && data.AuthDiags[key].RmiDiag !== 'RA' && data.AuthDiags[key].RmiDiag !== 'DR' && data.AuthDiags[key].RmiDiag !== 'UR' && data.AuthDiags[key].RmiDiag !== 'PR') {
          authDiagsData.push({
            key: key,
            ref: data.AuthDiags[key].RmiDiagRefNo,
            diagCode: data.AuthDiags[key].RmiDiag,
            desc: data.AuthDiags[key].RmiDiagDesc
          });
        }
      }

      for(const key in data.AuthDetails) {
        authDetailsData.push({
          key: key,
          cpt: data.AuthDetails[key].RmiProcCode,
          desc: data.AuthDetails[key].RmiDescr,
          mod: data.AuthDetails[key].RmiModif,
          diagRef: data.AuthDetails[key].RmiDiagCode,
          quantity: data.AuthDetails[key].RmiQty
        });
      }

      for(const key in data.AuthNotes) {
        authNotesData.push({
          key: key,
          subject: data.AuthNotes[key].RmiSubject,
          notes: data.AuthNotes[key].RmiNotes
        });
      }
      checkHospitalDates(data);

      setSearchAuthPageState((previousState: AuthModal) => ({
        ...previousState,
        authNo: data.RmiAuthNo,
        healthPlanAuthNumber: data.RmiHpAuthNo,
        requestDate: data.RmiReqDate,
        referenceType: data.AuthDiags[1]?.RmiDiagDesc,
        certificateType: data.RmiCertTypeDesc,
        actionDate: data.RmiAuthDate,
        expirationDate: data.RmiExprDate,
        retroDate: data.RmiAuthDate,
        companyId: data.RmiCompanyId,
        status: StatusCode[data.RmiStatus].toUpperCase(),
        statusNumber: data.RmiStatus,
        memberName: data.RmiMembName,
        memberPhoneNumber: formatPhoneNumber(data.RmiMembaddrPhone),
        healthPlan: data.RmiHpName,
        dateOfBirth: data.RmiBirth,
        gender: data.RmiSex,
        memberId: data.RmiMembId,
        memberPcp: data.RmiPcpRevFullName,
        referralBy: data.RmiRefRevFullName,
        referralByNumber: formatPhoneNumber(data.RmiReferByPhone),
        referralTo: data.RmiReqRevFullName,
        referralSpecialty: data.RmiReqSpec,
        referralPos: data.RmiPlaceSvc,
        referralByFaxNumber: formatPhoneNumber(data.RmiReferByFax),
        memberAddress: data.RmiMembaddrStreet,
        memberCity: data.RmiMembaddrCity,
        memberState: data.RmiMembaddrState,
        memberZipCode: data.RmiMembaddrZip,
        referralToAddress: data.RmiSpecaddrStreet,
        referralToCity: data.RmiSpecaddrCity,
        referralToState: data.RmiSpecAddrState,
        referralToZipCode: data.RmiSpecaddrZip,
        referralToPhoneNumber: formatPhoneNumber(data.RmiSpecaddrPhone),
        referralToFaxNumber: formatPhoneNumber(data.RmiSpecaddrFax),
        facilityName: data.FANAME,
        facilityAddress: data.FASTREET,
        facilityCity: data.FACITY,
        facilityState: data.FASTATE,
        facilityZipCode: data.FAZIP,
        facilityPhoneNumber: formatPhoneNumber(data.FAPHONE),
        authDiagsData: authDiagsData,
        authDetailsData: authDetailsData,
        authNotesData: authNotesData,
        authPcpId: data.RmiAuthPcp,
        requestProv: data.RmiReqProv,
        authPcpFirstName: authPcpFullName[1],
        authPcpLastName: authPcpFullName[0],
        requestProviderFirstName: requestProviderFullName[1],
        requestProviderLastName: requestProviderFullName[0],
        memberFirstName: memberFullName[1],
        memberLastName: memberFullName[0],
        priorityStatus: data.RmiPriorityStatus,
        rmiAdmitDate: data.RmiAdmitDate,
        rmiDschDate: data.RmiDschDate,
        rmiDischDisp: data.RmiDischDisp
      }));

      setIsModalVisible(true);
    }).catch((err: ErrorResponse) => {
      notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.');
    });

    props.getAttachInfo(apiData.authNum, apiData.companyId).then((attachmentData: Attachment) => {
      const attachmentArray: any = [];
      for (const key in attachmentData) {
        attachmentArray.push({
          authNo: attachmentData[key][0],
          companyId: attachmentData[key][1],
          folderLocation: attachmentData[key][2],
          fileName: attachmentData[key][3],
          fileMeta: attachmentData[key][4],
        });
      }

      setSearchAuthPageState((previousState: AuthModal) => ({
        ...previousState, attachments: attachmentArray
      }));

    }).catch((err: ErrorResponse) => {
      notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.');
    });
  }

  const onTableChangeHandler = (data: TablePagination) => {
    setTableInfo(data);
  }

  const NewTableDataHandler = (data: any) => {
    const tableData = [
      ...data
    ];

    const dataResult: any = [];
    let totalSearchResults = 0;

    // deferred = 4, pending = 7, and pending = 8 status have attachments

    for (const key in tableData) {
      const DEFERRED = StatusCode[tableData[key].RmiStatus].toUpperCase() === 'DEFERRED';
      const PENDING = StatusCode[tableData[key].RmiStatus].toUpperCase() === 'REQUESTED' || StatusCode[tableData[key].RmiStatus].toUpperCase() === 'SYSTEM_HOLD';
      let attachments: any;
      if(DEFERRED || PENDING) {
        attachments = <a href="#/" onClick={() => attachmentsFormModal({companyId: tableData[key].RmiCompanyId, authNum: tableData[key].RmiAuthNo})}>Add Attachment</a>
      }
      dataResult.push({
        key: key,
        companyId: tableData[key].RmiCompanyId,
        authNum: tableData[key].RmiAuthNo,
        memberRefId: tableData[key].RmiMembId,
        name: tableData[key].RmiMembName,
        providerRef: tableData[key].RPRmiRevFullName,
        status: StatusCode[tableData[key].RmiStatus]?.toUpperCase(),
        expDate: moment(tableData[key].RmiExprDate).format('MM/DD/YYYY'),
        attach: attachments,
        authNumSorter: tableData[key].RmiAuthNo,
      });
    }

    if(data.length > 0 && data[0].RmiCount) {
      totalSearchResults = data[0].RmiCount;
    }

    setTotalResultCount(totalSearchResults);
    setSearchResult(dataResult);
    sessionStorage.setItem('auth_search_result', JSON.stringify(dataResult));
  };

  const isModalVisibleHandler = () => {
    setIsModalVisible(false);
  }

  const triggerAuthDetailPage = (data: any) => {
    setState('auth-detail');
    setAuthDetail(data);
  }

  const triggerStartPage = () => {
    setState('start');
  }

  const authEnabledProviderListParam = {
    UserId: sessionStorage.user_id,
    UserRole: sessionStorage.user_role
  };

  useEffect(() => {
    props.getAuthEnabledProviderList(authEnabledProviderListParam)
    .then((response: GetAuthEnabledProviderListResponse) => {
      setProviderList(sortByIPA(response, 'Provider'))
    })
    .catch((err: ErrorResponse) => {
        notifier.error(err.response?.data.Message || 'Something went wrong, please contact a system administrator.');
    });

    savedSearchResults();
    getHelpCenterLinks();
  }, []);

  // This is to save the search result that will be used for the auth confirmation page
  const savedSearchResults = () => {
    if (location?.state?.previousPath === '/auth/modify-auth') {
      setSearchResult(JSON.parse(sessionStorage.getItem('auth_search_result')!));
    } else {
      setSearchResult([]);
      sessionStorage.removeItem('auth_search_result');
    }
  }

  const getHelpCenterLinks = () => {
    props.getHelpCenterLinks(sessionStorage.getItem('ipaGroup')!, '/auth/search', 'page')
      .then((response: any) => {
        setHelpCenter(response);
    })
    .catch((err: ErrorResponse) => {
      console.log(err?.response?.data?.Message);
    });
  }

  const checkHospitalDates = (authModal: any) => {
    let H5499Found = false;
    if (authModal.RmiPOS && H5499_POS_CODES.includes(authModal.RmiPOS.trim())) {
      H5499Found = authModal.AuthDetails.some((detail: any) => detail.RmiProcCode && detail.RmiProcCode.trim() === "H5499");

        //03/17/17 changed spec per meeting with CM, UM, and Dr. Wang
        if (authModal.RmiAuthDate && !H5499Found) {
            setShowHospitalDates(false);
        }
        else if (H5499Found && authModal.RmiSvcType !== "65") {
            setShowHospitalDates(true);
            authModal.AuthDetails.forEach((detail: any) => {
              if (detail.RmiProcCode != null) {
                var procCode = detail.RmiProcCode;
                //get admit and discharge date from detail line
                if (procCode.trim() === "H5499") {
                  authModal.RmiAdmitDate = detail.RmiAdmitDate;
                  authModal.RmiDschDate = detail.RmiDschDate;
                }
              }
            })
        }
    } else {
        setShowHospitalDates(false);
    }
  };

  const ExportToExcel = () => {
    const columnsToHide = ['attach', 'authNum', 'key'];
    const columnMapping: Record<string, string> = {
      companyId: 'IPA',
      memberRefId: 'Member ID',
      name: 'Member Name',
      providerRef: 'Refer To Provider',
      status: 'Status',
      expDate: 'Expiration Date',
      authNumSorter: 'Auth Number',
    };
    const columnOrder: string[] = ['IPA', 'Auth Number', 'Member ID', 'Member Name', 'Refer To Provider', 'Status', 'Expiration Date'];

    const exportToExcel = () => {
      const filteredData = searchResult.map(item => {
        const newItem: Record<string, any> = {};
        columnOrder.forEach(column => {
          const originalColumn = Object.keys(columnMapping).find(key => columnMapping[key] === column) || column;
          if (item[originalColumn] !== undefined && !columnsToHide.includes(originalColumn)) {
            newItem[column] = item[originalColumn];
          }
        });
        return newItem;
      });

      const worksheet = XLSX.utils.json_to_sheet(filteredData);
      const workbook = XLSX.utils.book_new();
      let fileName = 'Search_Auth_Data_' + sessionStorage.user_id + '_' + moment().format("MMDDYYYY") + '.xlsx';
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
      XLSX.writeFile(workbook, fileName);
    };

    return (
      <Button
        type="primary"
        icon={<CloudDownloadOutlined />}
        onClick={exportToExcel}
      >
        Export to Excel
      </Button>
    );
  };

  return (
    <div className={styles['search-authorization-page']}>
      <div className={styles['header-title']}>
          <Title level={4} className="ml-3">Search Authorization</Title>
      </div>
      <div>
        <Breadcrumb>
          <Breadcrumb.Item>
            <HomeOutlined
             onClick={() => history.push('/quality/overview')}
            />
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <SearchOutlined />
              <a href="#/" onClick={triggerStartPage}>Search</a>
          </Breadcrumb.Item>
          {
            state === 'auth-detail' &&
            <Breadcrumb.Item >
            <UserOutlined />
              <a href="#/" onClick={triggerAuthDetailPage}>Authorization Details</a>
            </Breadcrumb.Item>
          }
        </Breadcrumb>
        {
          helpCenter?.length > 0 ? helpCenter.map((hc: any) => {
            return (
              <>
                <div className={`row ${styles['help-center-link']}`}>
                  <div className="col-md-24">
                    <HelpCenterLink
                      clickEventId={hc.ClickEventId}
                      linkName={hc.LinkName}
                      pagePath={hc.PagePath}
                      url={hc.Link}
                      companyId={hc.CompanyId}
                    />
                  </div>
                </div>
              </>
            );
          }) : null
        }
      </div>

      {state === 'start' && (
        <div>
          <div className={styles['search-section']}>
            <SearchAuthForm
              getAuthList={props.getAuthList}
              onTableChange={tableInfo}
              newTableDataHandler={NewTableDataHandler}
              providerList={providerList}
              isFetchingList={props.isFetchingList}
              isFetchingAuthList={props.isFetchingAuthList}
            />
          </div>
          {searchResult && searchResult.length !== 0 && (
            <div className={styles['content']}>
              <div className={styles['header']}>
                <Title level={4}>Authorizations</Title>
                <ExportToExcel />
              </div>
              <SearchAuthorizationTable
                onTableChangeHandler={onTableChangeHandler}
                columns={columns}
                dataSource={searchResult}
                totalResultCount={totalResultCount}
                loading={props.isFetchingList || props.isFetchingListGroup}
              />
            </div>
          )}
        </div>
      )}
      {state === 'auth-detail' && (
        <AuthDetail
          providerList={providerList}
          authDetail={authDetail}
          triggerStartPage={triggerStartPage}
        />
      )}
      <AttachmentFormModal
        uploadFilestoAuth={props.uploadFilestoAuth}
        getSubmittedFileNames={props.getSubmittedFileNames}
        modifyAuth={props.modifyAuth}
        attachmentFormModal={searchAuthPageState}
        isModalVisible={isModalVisible}
        isModalVisibleHandler={isModalVisibleHandler}
        isUploadingFilesToAuth={props.isUploadingFilesToAuth}
        showHospitalDates={showHospitalDates}
      />
    </div>
  );
}

const mapStateToProps = (store: RootStateOrAny) => ({
  isFetchingList: store.rootReducer.authList.isFetchingList, //Search Authorization table
  isFetchingListGroup: store.rootReducer.authList.isFetchingListGroup,
  isFetchingAuthList: store.rootReducer.authProvider.isFetchingAuthEnabledList,
  isUploadingFilesToAuth: store.rootReducer.authFiles.isUploading,
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators(
  {
    getAuthEnabledProviderList,
    getPortalAuth,
    getAttachInfo,
    getAuthList,
    modifyAuth,
    getSubmittedFileNames,
    uploadFilestoAuth,
    getHelpCenterLinks,
  },
  dispatch,
);

export default connect(mapStateToProps, mapDispatchToProps)(SearchAuthPage);